import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Modal,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  IconButton,
  CircularProgress,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AddIcon from '@mui/icons-material/Add';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { useNavigate, Navigate } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import {
  useListStocktakesQuery,
  useCreateLocalStocktakeMutation,
  useStartStocktakeMutation,
} from '../../store/api/stocktakeApi';
import { useAuth } from '../../hooks/useAuth';
import { useDesktopModalInput } from '../../hooks/useDesktopModalInput';
import routes from '../../routes/routes';
import { dateInputDaysAgo, formatDateDisplayFromInput } from '../../helpers/dateHelpers';
import {
  loadStocktakeListFilters,
  saveStocktakeListFilters,
} from '../../helpers/stocktakeListFiltersStorage';
import BarcodeCameraDialog from '../picking/BarcodeCameraDialog';
import StocktakeDocumentCard from './StocktakeDocumentCard';
import WarehouseSelectDialog from './WarehouseSelectDialog';
import { documentCardSx, terminalBarIconButtonSx, terminalSelectSx, terminalSelectMenuProps } from '../../theme/terminalStyles';

const STATUS_FILTER = {
  ALL: 'all',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
};

const statusColor = (st) => (st.isDone ? '#2e7d32' : '#fff59d');

const triggerDatePicker = (input) => {
  if (!input?.showPicker) return;
  try {
    input.showPicker();
  } catch (e) {
    /* ignore */
  }
};

const openDatePicker = (event) => {
  triggerDatePicker(event.currentTarget.querySelector('input[type="date"]'));
};

const filterFormControlSx = {
  ...terminalSelectSx,
  flexShrink: 0,
  minWidth: { xs: 118, sm: 132 },
  '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
  '& .MuiSelect-select, & .MuiOutlinedInput-input': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    py: 0.75,
    fontWeight: 500,
  },
};

const dateFilterInputSx = {
  width: '100%',
  position: 'relative',
  fontWeight: 500,
  '& .MuiOutlinedInput-input': {
    width: '100%',
    minWidth: '10ch',
    px: 1.75,
    py: 0.75,
    fontVariantNumeric: 'tabular-nums',
    color: 'transparent',
    caretColor: 'transparent',
  },
  '& input[type="date"]::-webkit-datetime-edit': {
    padding: 0,
    margin: 0,
    visibility: 'hidden',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    opacity: 0,
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    cursor: 'pointer',
    background: 'transparent',
  },
};

const dateFilterDisplaySx = {
  position: 'absolute',
  left: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  color: 'text.primary',
  fontWeight: 500,
};

const readInitialListFilters = () => {
  const saved = loadStocktakeListFilters();
  return {
    dateFrom: saved?.dateFrom ?? dateInputDaysAgo(10),
    statusFilter: saved?.statusFilter ?? STATUS_FILTER.ALL,
  };
};

const StocktakeListPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  const [dateFrom, setDateFrom] = useState(() => readInitialListFilters().dateFrom);
  const [statusFilter, setStatusFilter] = useState(() => readInitialListFilters().statusFilter);

  useEffect(() => {
    saveStocktakeListFilters({ dateFrom, statusFilter });
  }, [dateFrom, statusFilter]);

  const [appliedFilter, setAppliedFilter] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [cameraSearchOpen, setCameraSearchOpen] = useState(false);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);

  const queryParams = useMemo(
    () => ({
      dateFrom,
      ...(appliedFilter ? { numberContains: appliedFilter } : {}),
    }),
    [dateFrom, appliedFilter]
  );

  const { data: stocktakes = [], isLoading, isFetching, error } = useListStocktakesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const filteredStocktakes = useMemo(() => {
    if (statusFilter === STATUS_FILTER.ALL) return stocktakes;
    if (statusFilter === STATUS_FILTER.DONE) return stocktakes.filter((s) => s.isDone);
    return stocktakes.filter((s) => !s.isDone);
  }, [stocktakes, statusFilter]);

  const [startStocktake, { isLoading: isStarting }] = useStartStocktakeMutation();
  const [createLocal, { isLoading: isCreating }] = useCreateLocalStocktakeMutation();

  if (!user?.terminalWorkplace?.isStockChecking) {
    return <Navigate to={routes.root} replace />;
  }

  const dialogText = (st) => {
    if (!st) return '';
    const label = st.fromERP ? st.number || String(st.id) : `#${st.id}`;
    if (st.isDone) return `Інвентаризацію ${label} завершено. Відкрити для перегляду?`;
    return `Відкрити інвентаризацію ${label}?`;
  };

  const handleConfirmOpen = async () => {
    if (!confirmDoc) return;
    const docId = confirmDoc.id;
    if (confirmDoc.isDone) {
      setConfirmDoc(null);
      navigate(`${routes.stocktake}/${docId}`);
      return;
    }
    try {
      await startStocktake(docId).unwrap();
      setConfirmDoc(null);
      navigate(`${routes.stocktake}/${docId}`);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateLocal = async (warehouseId) => {
    try {
      const res = await createLocal(warehouseId).unwrap();
      setWarehouseDialogOpen(false);
      setCreateConfirmOpen(false);
      const id = res.stocktake?.id;
      if (id) navigate(`${routes.stocktake}/${id}`);
    } catch (e) {
      console.error(e);
    }
  };

  const NumericSearchModal = () => {
    const [localVal, setLocalVal] = useState('');
    const keyboardRef = React.useRef(null);

    useEffect(() => {
      if (searchOpen) setLocalVal(appliedFilter);
    }, [searchOpen, appliedFilter]);

    const apply = () => {
      setAppliedFilter(localVal.trim());
      setSearchOpen(false);
    };

    const { inputRef, handleInputKeyDown } = useDesktopModalInput({
      open: searchOpen,
      onConfirm: apply,
      selectOnFocus: false,
    });

    return (
      <Modal open={searchOpen} onClose={() => setSearchOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '92%',
            maxWidth: 420,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Пошук за номером
          </Typography>
          <TextField
            fullWidth
            value={localVal}
            inputRef={inputRef}
            onKeyDown={handleInputKeyDown}
            onChange={(e) => setLocalVal(e.target.value)}
            margin="normal"
            inputProps={{ inputMode: 'numeric' }}
          />
          <Keyboard
            keyboardRef={(r) => {
              keyboardRef.current = r;
            }}
            onChange={setLocalVal}
            layout={{ default: ['1 2 3', '4 5 6', '7 8 9', '{clearAll} 0 {bksp}'] }}
            display={{ '{bksp}': '⌫', '{clearAll}': 'C' }}
            theme="hg-theme-default hg-layout-numeric"
            onKeyPress={(button) => {
              if (button === '{clearAll}') {
                setLocalVal('');
                keyboardRef.current?.clearInput();
              }
            }}
          />
          <TerminalOrangeButton
            fullWidth
            sx={{ mt: 1 }}
            startIcon={<PhotoCameraIcon />}
            onClick={() => {
              setSearchOpen(false);
              setCameraSearchOpen(true);
            }}
          >
            Сканувати з камери
          </TerminalOrangeButton>
          <TerminalOrangeButton fullWidth sx={{ mt: 2 }} onClick={apply}>
            OK
          </TerminalOrangeButton>
        </Box>
      </Modal>
    );
  };

  const bottomBar = (
    <>
      <IconButton aria-label="пошук" onClick={() => setSearchOpen(true)} sx={terminalBarIconButtonSx}>
        <SearchIcon />
      </IconButton>
      <IconButton
        aria-label="камера"
        onClick={() => setCameraSearchOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <PhotoCameraIcon />
      </IconButton>
      {appliedFilter ? (
        <IconButton
          aria-label="очистити фільтр"
          onClick={() => setAppliedFilter('')}
          sx={terminalBarIconButtonSx}
        >
          <FilterAltOffIcon />
        </IconButton>
      ) : null}
      <IconButton
        aria-label="створити"
        onClick={() => setCreateConfirmOpen(true)}
        sx={{ ...terminalBarIconButtonSx, ml: 'auto' }}
      >
        <AddIcon />
      </IconButton>
    </>
  );

  return (
    <TerminalLayout
      title="Інвентаризація"
      titleIcon={<FactCheckOutlinedIcon />}
      backTo={routes.root}
      bottomBar={bottomBar}
    >
      <BarcodeCameraDialog
        open={cameraSearchOpen}
        onClose={() => setCameraSearchOpen(false)}
        onCode={(text) => {
          setAppliedFilter(String(text || '').trim());
        }}
        title="Сканувати номер / штрихкод"
      />
      <NumericSearchModal />

      <Paper sx={{ ...documentCardSx, mb: 1.5, py: 1, px: 1.5 }} elevation={0}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            width: '100%',
            minWidth: 0,
          }}
        >
          <FormControl
            size="small"
            sx={{ ...filterFormControlSx, minWidth: { xs: 132, sm: 148 } }}
            onClick={openDatePicker}
          >
            <InputLabel htmlFor="stocktake-date-from-filter" shrink>
              З дати
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <OutlinedInput
                id="stocktake-date-from-filter"
                type="date"
                label="З дати"
                notched
                value={dateFrom}
                onChange={(e) => {
                  if (e.target.value) setDateFrom(e.target.value);
                }}
                onKeyDown={(e) => e.preventDefault()}
                sx={dateFilterInputSx}
                inputProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                    triggerDatePicker(e.currentTarget);
                  },
                }}
              />
              <Typography component="span" aria-hidden sx={dateFilterDisplaySx}>
                {formatDateDisplayFromInput(dateFrom)}
              </Typography>
            </Box>
          </FormControl>
          <FormControl size="small" sx={{ ...filterFormControlSx, ml: 'auto' }}>
            <InputLabel id="stocktake-status-filter-label">Статус</InputLabel>
            <Select
              labelId="stocktake-status-filter-label"
              label="Статус"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              MenuProps={terminalSelectMenuProps}
            >
              <MenuItem value={STATUS_FILTER.ALL}>Всі</MenuItem>
              <MenuItem value={STATUS_FILTER.IN_PROGRESS}>В роботі</MenuItem>
              <MenuItem value={STATUS_FILTER.DONE}>Завершені</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">Не вдалося завантажити список</Typography>
      ) : (
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {filteredStocktakes.map((st) => (
            <StocktakeDocumentCard
              key={st.id}
              stocktake={st}
              statusColor={statusColor(st)}
              onOpen={() => setConfirmDoc(st)}
            />
          ))}
          {!filteredStocktakes.length ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Документів не знайдено
            </Typography>
          ) : null}
        </Box>
      )}

      {isFetching && !isLoading ? (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Оновлення…
        </Typography>
      ) : null}

      <Dialog open={Boolean(confirmDoc)} onClose={() => setConfirmDoc(null)}>
        <DialogTitle>Підтвердження</DialogTitle>
        <DialogContent>
          <Typography>{confirmDoc ? dialogText(confirmDoc) : ''}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setConfirmDoc(null)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleConfirmOpen} disabled={isStarting}>
            {isStarting ? <CircularProgress size={22} color="inherit" /> : 'Так'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={createConfirmOpen} onClose={() => setCreateConfirmOpen(false)}>
        <DialogTitle>Нова інвентаризація</DialogTitle>
        <DialogContent>
          <Typography>Створити локальний документ інвентаризації?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setCreateConfirmOpen(false)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton
            onClick={() => {
              setCreateConfirmOpen(false);
              setWarehouseDialogOpen(true);
            }}
          >
            Так
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <WarehouseSelectDialog
        open={warehouseDialogOpen}
        onClose={() => setWarehouseDialogOpen(false)}
        onConfirm={handleCreateLocal}
        isSubmitting={isCreating}
      />
    </TerminalLayout>
  );
};

export default StocktakeListPage;
